import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Plus, LayoutGrid, List, ChevronDown } from "lucide-react";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { CategoryList } from "../components/CategoryList";
import ProductList from "../components/ProductList";
import { AddMenuItemForm } from "../components/AddMenuItemForm";
import { AddCategoryForm } from "../components/AddCategoryForm";
import MenuLoader from "../components/MenuLoader";
import store from "../store";
import { useCloud } from "../hooks/useCloud";
import toast from "react-hot-toast";
import { SUCCESS } from "../assets/constants";

export default function MenuManagement() {
  const {
    addItemAPI,
    updateItem,
    deleteItem,
    categoryDelete,
    categoryUpdate,
    addCategoryAPI,
    outOfStock,
    getMenu,
    createOrUpdateCategory,
    createOrUpdateMenuItem,
    deleteMenuItem,
    deleteCategory,
    updateCategoryRank,
    updateItemsRank,
  } = useCloud();
  // Sample data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [forceUpdateCount, setForeceUpdateCount] = useState(0);
  const [viewMode, setViewMode] = useState("card");
  const [isCategoryShufelled, setIsCategoryShufelled] = useState(false);
  const [isItemsShufelled, setIsItemsShufelled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const processMenu = (menu) => {
    let { products, categories } = menu.reduce(
      (acc, category) => {
        acc.products.push(
          ...category.menuItems.map((item) => {
            item.category = category.categoryName;
            item.categoryDetails = category;
            return item;
          })
        );
        if (
          !acc.categories.some(
            (_category) => _category.categoryId == category.categoryId
          )
        )
          acc.categories.push(category);
        return acc;
      },
      {
        products: [],
        categories: [],
      }
    );

    categories = categories.sort((a, b) => a.categoryRank - b.categoryRank);
    setLoading(false);
    return { products, categories };
  };

  const fetchMenu = async (addLoading = true) => {
    if (addLoading) setLoading(true);
    try {
      const menu = await getMenu();
      if (menu && menu.status === SUCCESS) {
        const { data } = menu;
        if (data.length > 0) {
          const { products, categories } = processMenu(data);

          setCategories(
            categories.sort((a, b) => a.categoryRank - b.categoryRank)
          );
          setSelectedCategory(selectedCategory || categories[0]);
          setProducts(products?.sort((a, b) => a.rank - b.rank));
          setProductMap(
            products.reduce((acc, item) => {
              acc[item.id] = item;
              return acc;
            }, {})
          );
          setFilteredProducts(
            products?.filter(
              (product) => product.categoryId == selectedCategory?.categoryId
            )
          );
        }
        // setProductDetailsMap(productDeatilsMap);
      }
    } catch (e) {
      console.log(e);
    } finally {
      if (addLoading) setLoading(false);
    }
  };

  useEffect(() => {
    const mode = localStorage.getItem("menu-view");
    if (mode) setViewMode(mode);
  }, []);

  useEffect(() => {
    fetchMenu();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".category-dropdown")) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filteredProducts = products
      ?.filter((product) => {
        // Then filter by search query if it exists
        if (!searchQuery) {
          return product.categoryId == selectedCategory?.categoryId;
        }

        const query = searchQuery.toLowerCase();
        return (
          product.itemName?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.rank - b.rank);

    setFilteredProducts(filteredProducts);

    const categoryIdSet = new Set(
      filteredProducts.map((product) => product.categoryId)
    );

    if (searchQuery && filteredProducts.length === 1) {
      setSelectedCategory(
        categories.find(
          (category) => category.categoryId === filteredProducts[0].categoryId
        )
      );
    } else if (categoryIdSet.size === 1) {
      setSelectedCategory(
        categories.find(
          (category) =>
            category.categoryId === categoryIdSet.values().next().value
        )
      );
    }
  }, [selectedCategory, products, searchQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChangeViewMode = (mode) => {
    localStorage.setItem("menu-view", mode);
    setViewMode(mode);
  };

  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setIsCategoryShufelled(true);
      setCategories((items) => {
        const oldIndex = items.findIndex(
          (item) => item.categoryId === active.id
        );
        const newIndex = items.findIndex((item) => item.categoryId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleProductDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setIsItemsShufelled(true);
      setProducts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        items[oldIndex].rank = newIndex;
        items[newIndex].rank = oldIndex;

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleEditProduct = (id) => {
    // Implement edit functionality
    console.log(`Edit product with id: ${id}`);
    console.log(productMap[id]);
    setSelectedProduct(productMap[id]);
    setIsProductFormOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    const { status, data } = await deleteMenuItem(id);
    if (status === SUCCESS) {
      setProducts(products.filter((product) => product.id !== id));
      toast.success("Menu Item deleted successfully");
    } else {
      toast.error("Failed! Please try again");
    }
  };

  const updateProductList = (products, updatedItem) => {
    return products.map((item) =>
      item.id === updatedItem.id ? updatedItem : item
    );
  };

  const addItemToProductMap = (item) => {
    setProductMap((prev) => ({
      ...prev,
      [item.id]: item,
    }));
  };

  const handleAPIResponse = (newProduct, response, isUpdate = false) => {
    const { menuItem, event, msg = "", error = "" } = response;

    if (event) {
      const updatedItem = {
        ...newProduct,
        ...menuItem,
        amount: menuItem.price,
      };

      if (isUpdate) {
        setProducts((prev) => updateProductList(prev, updatedItem));
      } else {
        setProducts((prev) => [...prev, updatedItem]);
      }

      addItemToProductMap(updatedItem);
      store.addItems(
        isUpdate
          ? updateProductList(products, updatedItem)
          : [...products, updatedItem]
      );

      toast.success(msg);
    } else {
      toast.error(error);
    }
  };

  const handleAddProduct = async (newProduct, callBack = () => {}) => {
    if (selectedProduct) newProduct["id"] = selectedProduct.id;
    newProduct.rank =
      selectedProduct?.rank !== undefined
        ? selectedProduct?.rank
        : products.length + 1;
    const { status, data } = await createOrUpdateMenuItem(newProduct);
    if (status === SUCCESS) {
      if (selectedProduct) {
        setProducts((prev) => {
          if (selectedProduct) {
            toast.success("Menu updated successfully");
            return prev.map((menuItem) => {
              if (menuItem.id === newProduct.id) {
                return {...newProduct, images: selectedProduct.images};
              }
              return menuItem;
            });
          }
          return prev;
        });
        setProductMap((prev) => ({
          ...prev,
          [newProduct.id]: {...newProduct, images: selectedProduct.images},
        }));
      } else {
        fetchMenu(false);
        toast.success("Menu added successfully");
      }

      setSelectedProduct(null);
      callBack();
    }
    setIsProductFormOpen(false);
  };

  const handleAddCategory = async (newCategory) => {
    if (selectedCategory) {
      newCategory["categoryId"] = selectedCategory.categoryId;
    } else {
      newCategory.rank = categories.length + 1;
    }
    const { status, data } = await createOrUpdateCategory({...newCategory});

    if (status === SUCCESS) {
      setCategories((prev) => {
        if (selectedCategory) {
          toast.success("Category updated successfully");
          return prev.map((category) => {
            if (category.categoryId === newCategory.categoryId) {
              return newCategory;
            }
            return category;
          });
        } else {
          const category = { ...newCategory, categoryId: data.categoryId };
          toast.success("Category added successfully");
          setSelectedCategory(category);
          return [...prev, { ...newCategory, ...category }];
        }
      });
      setIsCategoryFormOpen(false);
    }
  };

  const handleCategoryEdit = (category) => {
    setIsCategoryFormOpen(true);
  };
  const handleCategoryDelete = async (categoryId) => {
    console.log(categoryId);

    const { status, data } = await deleteCategory(categoryId);
    if (status === SUCCESS) {
      const filterdCategories = categories.filter(
        (category) => category.categoryId !== categoryId
      );
      setCategories(filterdCategories);
      setSelectedCategory(filterdCategories[0]);
      toast.success("Menu Item deleted successfully");
    } else {
      toast.error("Failed! Please try again");
    }
  };

  const handelCategoryStatusUpdate = async (category, updateStatus) => {
    const newCategory = {
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      image: category.categoryImage,
      status: updateStatus,
    };

    const { status, data } = await createOrUpdateCategory(newCategory);

    if (status === SUCCESS) {
      setCategories((prev) => {
        if (selectedCategory) {
          toast.success("Category updated successfully");
          return prev.map((category) => {
            if (category.categoryId === newCategory.categoryId) {
              return { ...newCategory, categoryImage: newCategory.image };
            }
            return category;
          });
        } else {
          const category = { ...newCategory, categoryId: data.categoryId };
          toast.success("Category added successfully");
          setSelectedCategory(category);
          return [...prev, { ...newCategory, category }];
        }
      });
      setIsCategoryFormOpen(false);
    }
  };

  const handleUpdateCategory = (category) => {
    console.log(category);
    // const res = await categoryUpdate({
    //   name: form.name,
    //   categoryId: form.id,
    //   url: form.url,
    // });
  };

  const handleCloseAddProductForm = () => {
    setIsProductFormOpen(false);
    setSelectedProduct(null);
  };

  const toggleStock = async (product) => {
    const { id, categoryId } = product;
    const status = product.status === "active" ? "inactive" : "active";
    const { status: responseStatus } = await outOfStock({
      itemId: id,
      categoryId: categoryId,
      status,
    });
    if (responseStatus === SUCCESS) {
      setProducts((prev) => {
        prev.forEach((item) => {
          if (item.id === id) {
            item.status = status;
          }
        });
        return prev;
      });

      setForeceUpdateCount((prev) => prev + 1);
      toast.success("Stock updated");
    } else {
      toast.error("Please try again");
    }
  };

  const handleCategorRankSave = async () => {
    const ranks = categories.map((item, index) => {
      return {
        categoryId: item.categoryId,
        rank: index,
      };
    });

    const { status } = await updateCategoryRank(ranks);
    if (status === SUCCESS) {
      toast.success("Category order updated successfully");
      await fetchMenu(false);
    }
  };

  const handleItemRankSave = async () => {
    const ranks = products.map((item, index) => {
      return {
        itemId: item.id,
        categoryId: item.categoryId,
        rank: index,
      };
    });

    const { status } = await updateItemsRank(ranks);
    if (status === SUCCESS) {
      toast.success("Food items order updated successfully");
      await fetchMenu(false);
    }
  };

  return (
    <MenuLoader isLoading={loading}>
      <div className="flex">
        <div className="flex flex-1 flex-col">
          <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-gray-300 bg-white px-4 sm:px-6">
            <h1 className="text-lg sm:text-xl font-semibold">
              Menu Management
            </h1>
          </header>

          <div className="bg-gray-50">
            <div className="h-[calc(100vh-64px)] w-full mx-auto p-2 sm:p-4 box-border bg-gray-50 overflow-x-hidden relative">
              {/* Mobile Layout */}
              <div className="lg:hidden flex flex-col space-y-4 h-full">
                {/* Mobile Category Dropdown */}
                <div className="category-dropdown">
                  <div className="relative">
                    <button
                      onClick={() =>
                        setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                      }
                      className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {selectedCategory
                          ? selectedCategory.categoryName
                          : "Select Category"}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          isCategoryDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isCategoryDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                        {categories.map((category) => (
                          <button
                            key={category.categoryId}
                            onClick={() => {
                              setSelectedCategory(category);
                              setIsCategoryDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                              selectedCategory?.categoryId ===
                              category.categoryId
                                ? "bg-blue-50 text-blue-700 border-l-4 border-blue-500"
                                : "text-gray-900"
                            }`}
                          >
                            {category.categoryName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile Product Section */}
                <div className="flex flex-col space-y-4 flex-1">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium">Category</h2>
                    {isCategoryShufelled ? (
                      <button
                        onClick={() => {
                          handleCategorRankSave();
                          setIsCategoryShufelled(false);
                        }}
                        className="h-[38px] px-3 text-sm text-white hover:text-black border bg-black border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                      >
                        Save shuffle
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedCategory(null);
                          setIsCategoryFormOpen(true);
                        }}
                        className="flex bg-white items-center justify-center h-[38px] px-3 text-sm border border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </button>
                    )}
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleCategoryDragEnd}
                    modifiers={[restrictToParentElement]}
                  >
                    <CategoryList
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onSelectCategory={setSelectedCategory}
                      onEdit={handleCategoryEdit}
                      onDelete={handleCategoryDelete}
                      onStatusChange={handelCategoryStatusUpdate}
                    />
                  </DndContext>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden lg:grid grid-cols-1 gap-4 lg:gap-6 lg:grid-cols-[280px_1fr] h-full">
                {/* Desktop Category Sidebar */}
                <div className="flex flex-col space-y-4 h-full">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium">Category</h2>
                    {isCategoryShufelled ? (
                      <button
                        onClick={() => {
                          handleCategorRankSave();
                          setIsCategoryShufelled(false);
                        }}
                        className="h-[38px] px-3 text-sm text-white hover:text-black border bg-black border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                      >
                        Save shuffle
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedCategory(null);
                          setIsCategoryFormOpen(true);
                        }}
                        className="flex bg-white items-center justify-center h-[38px] px-3 text-sm border border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </button>
                    )}
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleCategoryDragEnd}
                    modifiers={[restrictToParentElement]}
                  >
                    <CategoryList
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onSelectCategory={setSelectedCategory}
                      onEdit={handleCategoryEdit}
                      onDelete={handleCategoryDelete}
                      onStatusChange={handelCategoryStatusUpdate}
                    />
                  </DndContext>
                </div>
                {/* Desktop Product Section */}
                <div className="flex flex-col space-y-4 h-full">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h2 className="text-lg font-medium">Food Items</h2>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative w-full sm:w-auto sm:min-w-[300px]">
                        <input
                          type="text"
                          placeholder="Search menu"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg
                            className="h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      <button
                        data-testid="add-food-item-button"
                        onClick={() => setIsProductFormOpen(true)}
                        className="flex bg-white items-center justify-center h-[38px] px-3 text-sm border border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Food Item
                      </button>

                      <div className="inline-flex rounded-md shadow-sm w-full sm:w-auto justify-center">
                        <button
                          type="button"
                          onClick={() => handleChangeViewMode("card")}
                          className={`relative inline-flex items-center px-2 py-1 text-sm font-medium rounded-l-md ${
                            viewMode === "card"
                              ? "bg-gray-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          } border border-gray-300`}
                        >
                          <LayoutGrid className="h-4 w-4 mr-2" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChangeViewMode("table")}
                          className={`relative inline-flex items-center px-2 py-1 text-sm font-medium rounded-r-md ${
                            viewMode === "table"
                              ? "bg-gray-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          } border border-gray-300 border-l-0`}
                        >
                          <List className="h-4 w-4 mr-2" />
                        </button>
                      </div>

                      {isItemsShufelled && (
                        <button
                          onClick={() => {
                            handleItemRankSave();
                            setIsItemsShufelled(false);
                          }}
                          className="h-[38px] px-3 text-sm text-white hover:text-black border bg-black border-gray-300 rounded-md hover:border-gray-300 hover:bg-gray-50"
                        >
                          Save shuffle
                        </button>
                      )}
                    </div>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleProductDragEnd}
                    modifiers={[restrictToParentElement]}
                  >
                    <ProductList
                      products={filteredProducts}
                      onEdit={handleEditProduct}
                      onDelete={handleDeleteProduct}
                      viewMode={viewMode}
                      toggleStock={toggleStock}
                    />
                  </DndContext>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Add Product Form Modal */}
        {isProductFormOpen && (
          <AddMenuItemForm
            menuItem={selectedProduct}
            categories={categories}
            selectedCategory={selectedCategory}
            onClose={handleCloseAddProductForm}
            onSubmit={handleAddProduct}
          />
        )}

        {/* Add Category Form Modal */}
        {isCategoryFormOpen && (
          <AddCategoryForm
            categoryItem={selectedCategory}
            onClose={() => {
              setSelectedCategory(categories[0]);
              setIsCategoryFormOpen(false);
            }}
            onSubmit={handleAddCategory}
          />
        )}
      </div>
    </MenuLoader>
  );
}
